// Distributions of arrivals: how the arrivals of the selected cells or areas split across the bands of one measure
// (distance, travel time, visit length, purpose, origin). The data API's activity_distributions rows are one per
// (area, hour, role, measure, band); this sums them per hour, group and band, as the demo API does
// (app-backend-demo/lib/eeDistributions.ts). Arithmetic only: the bands are modelled and shown in full, a band under
// 5 included. K=5 is applied upstream, on the place's arrivals, so the app adds no privacy rule (docs/adr/0001).
// Pure: no React, router or network.
import { rowAreaKey } from '../services/catalogueRequests';
import { nullableNumber, unwrap } from '../services/rowValues';
import { parseBucket } from './dateWindow';
import { EE_DISTRIBUTION_MEASURES, EE_ROLES } from './eeActivities';
import type { EeDistribution, EeDistributionBand, EeDistributionGroup, EeDistributionMeasure } from './eeActivities';

/** One distribution row, as far as the sums need it. */
export interface DistributionRow {
  /** As the saved area names it: a grid cell's key ('grid_500m:<id>'), an admin area's own id. */
  geo_id: string;
  hour_of_day: number | null;
  role: string;
  measure: string;
  band: string;
  band_name: string | null;
  band_min: number | null;
  band_max: number | null;
  unit: string | null;
  people: number | null;
  arrivals: number | null;
}

const NUMERIC_MEASURES = new Set(['distance', 'travel_time', 'visit_length']);

type Num = number | null;

/** Sum that ignores nulls; null when every input is null. */
function nullSum(values: Num[]): Num {
  let sum: Num = null;
  for (const v of values) if (v !== null) sum = (sum ?? 0) + v;
  return sum;
}

const optionalText = (v: unknown): string | null => {
  const u = unwrap(v);
  return u == null ? null : String(u);
};

/**
 * The hour of a row: the UTC hour of its time bucket on an hourly answer, none on a daily one. The data API sends
 * the bucket (an ISO instant) rather than an hour column; an hour_of_day column is used when a row has one.
 */
export function rowHour(raw: Record<string, unknown>, type: 'daily' | 'hourly'): number | null {
  if (type === 'daily') return null;
  const own = nullableNumber(raw.hour_of_day);
  if (own !== null) return own;
  const ms = parseBucket(raw.time_bucket);
  return ms === null ? null : new Date(ms).getUTCHours();
}

/** A data API distribution row as a DistributionRow; null for a row with no area id. */
export function toDistributionRow(raw: unknown, type: 'daily' | 'hourly'): DistributionRow | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = unwrap(r.geo_id);
  if (id == null || String(id) === '') return null;
  return {
    geo_id: rowAreaKey(id, unwrap(r.geo_type)),
    hour_of_day: rowHour(r, type),
    role: String(unwrap(r.role)),
    measure: String(unwrap(r.measure)),
    band: String(unwrap(r.band)),
    band_name: optionalText(r.band_name),
    band_min: nullableNumber(r.band_min),
    band_max: nullableNumber(r.band_max),
    unit: optionalText(r.unit),
    people: nullableNumber(r.people),
    arrivals: nullableNumber(r.arrivals),
  };
}

/** Numeric measures by lower edge, the others by arrivals (nulls after numbers). */
function compareBands(measure: string, a: EeDistributionBand, b: EeDistributionBand): number {
  let c = 0;
  if (NUMERIC_MEASURES.has(measure)) {
    if (a.min !== b.min) c = a.min === null ? 1 : b.min === null ? -1 : a.min - b.min;
  } else if (a.arrivals !== b.arrivals) {
    c = a.arrivals === null ? 1 : b.arrivals === null ? -1 : b.arrivals - a.arrivals;
  }
  return c || a.band.localeCompare(b.band, undefined, { numeric: true });
}

interface Acc {
  hour: number | null;
  group: EeDistributionGroup;
  measure: string;
  unit: string | null;
  bands: Map<string, { name: string | null; min: Num; max: Num; arrivals: Num[]; people: Num[] }>;
}

const GROUP_ORDER: Record<EeDistributionGroup, number> = { everyone: 0, home: 1, work: 2, school: 3, elsewhere: 4 };
const MEASURE_ORDER: readonly string[] = EE_DISTRIBUTION_MEASURES;
const rank = (m: string) => (MEASURE_ORDER.includes(m) ? MEASURE_ORDER.indexOf(m) : MEASURE_ORDER.length);

/**
 * The distributions of the given rows, which must already be limited to the cells or areas that count. Per
 * (geo, hour, measure) "everyone" uses the role "all" rows when there are any, else the sum of the roles; each role
 * (home, work, school, elsewhere) is a group of its own. Band arrivals and people are summed over geos, ignoring
 * nulls. A measure the dashboard has no panel for is left out.
 */
export function buildDistributions(rows: readonly DistributionRow[]): EeDistribution[] {
  // Which (geo, hour, measure) have role "all" rows.
  const hasAll = new Set<string>();
  const slot = (r: DistributionRow) => `${r.geo_id}|${r.hour_of_day ?? ''}|${r.measure}`;
  for (const r of rows) if (r.role === 'all') hasAll.add(slot(r));

  const groups = new Map<string, Acc>();
  const add = (group: EeDistributionGroup, r: DistributionRow) => {
    const key = `${r.hour_of_day ?? ''}|${group}|${r.measure}`;
    let acc = groups.get(key);
    if (!acc) groups.set(key, (acc = { hour: r.hour_of_day, group, measure: r.measure, unit: r.unit, bands: new Map() }));
    if (acc.unit === null) acc.unit = r.unit;
    let b = acc.bands.get(r.band);
    if (!b) acc.bands.set(r.band, (b = { name: r.band_name, min: r.band_min, max: r.band_max, arrivals: [], people: [] }));
    b.arrivals.push(r.arrivals);
    b.people.push(r.people);
  };
  for (const r of rows) {
    if (!MEASURE_ORDER.includes(r.measure)) continue;
    if (r.role === 'all' || !hasAll.has(slot(r))) add('everyone', r);
    if ((EE_ROLES as readonly string[]).includes(r.role)) add(r.role as EeDistributionGroup, r);
  }

  const out: EeDistribution[] = [];
  groups.forEach((acc) => {
    const bands: EeDistributionBand[] = [];
    acc.bands.forEach((b, band) =>
      bands.push({ band, name: b.name ?? band, min: b.min, max: b.max, arrivals: nullSum(b.arrivals), people: nullSum(b.people), share: null }),
    );
    const total = nullSum(bands.map((b) => b.arrivals));
    for (const b of bands) b.share = total === null || b.arrivals === null || total === 0 ? null : Math.round((b.arrivals / total) * 1e4) / 1e4;
    bands.sort((a, b) => compareBands(acc.measure, a, b));
    out.push({
      hour: acc.hour,
      group: acc.group,
      measure: acc.measure as EeDistributionMeasure,
      unit: acc.unit ?? '',
      arrivals: total,
      bands,
    });
  });
  return out.sort(
    (a, b) =>
      (a.hour ?? -1) - (b.hour ?? -1) || GROUP_ORDER[a.group] - GROUP_ORDER[b.group] || rank(a.measure) - rank(b.measure),
  );
}
